import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil, startWith, switchMap } from 'rxjs/operators';

import { Loggable } from 'app/utils/logging/loggable';
import { resettableSubject } from 'app/utils/stream-util';
import SocialArtifact from 'entity/SocialArtifact';
import SocialController from 'rest/SocialController';

import { actions, selectors } from '../../state/app';

export enum ArtifactOperationType {
  ADD = 'add',
  REMOVE = 'remove',
  CLEAR = 'clear',
}

interface ArtifactOperationBase {
  type: ArtifactOperationType;
}

export interface AddArtifact extends ArtifactOperationBase {
  type: ArtifactOperationType.ADD;
  artifact: SocialArtifact;
}

export interface RemoveArtifact extends ArtifactOperationBase {
  type: ArtifactOperationType.REMOVE;
  artifact: SocialArtifact;
}

export interface ClearArtifacts extends ArtifactOperationBase {
  type: ArtifactOperationType.CLEAR;
}

export type ArtifactOperation = AddArtifact | RemoveArtifact | ClearArtifacts;

@Injectable({
  providedIn: 'root',
})
export class ArtifactService extends Loggable {
  private destroyed$ = new ReplaySubject<boolean>(1);
  private artifactOperation = resettableSubject(() => new ReplaySubject<ArtifactOperation>(Infinity));
  private socialId?: string;

  public get artifactsOperation$(): Observable<ArtifactOperation> {
    return this.artifactOperation.observable;
  }

  public constructor(private store: Store) {
    super();
    this.setDisplayName('ArtifactService');
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject<boolean>(1);
    this.store
      .select(selectors.getJoinedSocialId)
      .pipe(
        startWith(''),
        switchMap(async (socialId: string | undefined) => {
          if (!socialId) {
            this.artifactOperation.reset();
            this.clearArtifacts();
            return;
          }

          if (socialId !== this.socialId) {
            this.artifactOperation.reset();
          }

          this.socialId = socialId;
          this.clearArtifacts();
          await this.retrieveArtifacts(socialId);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }
  private clearArtifacts(): void {
    this.store.dispatch(actions.SET_ARTIFACTS({ artifacts: [] }));
    this.artifactOperation.subject.next({ type: ArtifactOperationType.CLEAR });
    // Assets.reset();
  }

  private async retrieveArtifacts(socialId: string): Promise<void> {
    this.trace('Fetching social artifacts');
    const socialController = new SocialController();
    try {
      const artifacts = await socialController.getSocialArtifacts(socialId);
      this.store.dispatch(actions.SET_ARTIFACTS({ artifacts }));

      artifacts.forEach((artifact: SocialArtifact) => {
        this.artifactOperation.subject.next({ type: ArtifactOperationType.ADD, artifact });
      });
    } catch (error) {
      this.error('failed to get artifacts', error);
      return;
    }
  }

  public destroy(): void {
    // Assets.reset();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
